const { getElement, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

/**
 * @event BaoVariants#variant:change
 * @type {object}
 * @property {Variant} variant
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockTrigger: '[data-product-bis-trigger]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  stockMessage: '[data-stock-message]',
  wishlistButton: '[data-wishlist-button]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  /**
   * Renders the context object and updates the input.
   *
   * @param {HTMLElement|BaoVariants} context
   * @param {boolean} [updateUrl=false] - Indicates whether to update the URL.
   * @return {void}
   */
  render (context, { updateUrl = false } = {}) {
    this.#setAvailability(context)
    this.setPrice(context)
    this.#updateInput(context)

    if (updateUrl) {
      this.#setUrl()
      this.#setShareUrl(context)
    }

    if (context.gallery) {
      this.#setSlide(context.gallery)
    }
  }

  /**
   * @param {HTMLElement[]} elements
   */
  #hideElements (elements) {
    elements.forEach(el => (el.hidden = true))
  }

  /**
   * @param {HTMLElement[]} elements
   */
  #unhideElements (elements) {
    elements.forEach(el => (el.hidden = false))
  }

  /**
   * @param {HTMLElement|BaoVariants} context - The context in which we'll find price objects.
   */
  #unhidePriceEls (context) {
    this.#unhideElements(
      getElements(`${selectors.price}, ${selectors.priceCompare}`, { context }),
    )
  }

  /**
   * @param {HTMLElement|BaoVariants} context - The context in which we'll find price objects.
   */
  hidePriceCompareElements (context) {
    this.#hideElements(getElements(selectors.priceCompare, { context }))
  }

  /**
   * Sets the price of the product.
   *
   * @param {HTMLElement|BaoVariants} context - The context in which we'll find the elements.
   */
  async setPrice (context) {
    const url = new URL(window.location.href)
    url.pathname = context.baseUrl
    url.searchParams.set('variant', this.id.toString())

    const priceElements = getElements(selectors.price, { context })
    if (priceElements.length === 0) return

    const html = await window.BAO.Utils.renderProductPrice(url.toString())

    this.#unhidePriceEls(context)

    priceElements.forEach(el => (el.innerHTML = html))

    if (!this.data.compare_at_price) {
      this.hidePriceCompareElements(context)
    }

    const yotpoPointElement = getElement('[loyalty-points]', { context })

    if (!yotpoPointElement) return

    yotpoPointElement.dataset.variantId = this.id
  }

  /**
   * Sets the availability of an item in the UI.
   * This method disables or enables the "Add to Cart" button and updates the text based on availability data.
   *
   * @param {HTMLElement|BaoVariants} context - The context in which we'll find the elements.
   */
  #setAvailability (context) {
    getElements(`${selectors.backInStockTrigger}`).forEach(el => {
      el.setAttribute('aria-hidden', this.data.available)
      el.setAttribute(
        'data-product-url',
        `${this.url.toString()}?variant=${this.data.id}`,
      )
    })

    getElements(selectors.addToCart, { context }).forEach(el => {
      el.disabled = !this.data.available
      el.setAttribute('aria-hidden', !this.data.available)
    })

    getElements(selectors.addToCartText, { context }).forEach(
      el =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )

    getElements(selectors.stockMessage, { context }).forEach(el => {
      el.textContent = this.data.available
        ? window.theme.strings.inStock
        : window.theme.strings.outOfStock
    })
  }

  /**
   * Updates input fields in a form with the specified ID value.
   * The form fields should have a "name" attribute equal to "id" and should be located within a form with an "action" attribute containing "/cart/add".
   *
   * @param {HTMLElement|BaoVariants} context - The context in which we'll find the elements.
   */
  #updateInput (context) {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`, {
      context,
    })

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })

    const wishlistButtons = getElements(selectors.wishlistButton, {
      context,
    })

    wishlistButtons.forEach(wishlistButton => {
      wishlistButton.dataset.variantId = this.id
    })
  }

  #setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  /**
   * Sets the share URL for all share buttons within the context.
   *
   * @param {HTMLElement|BaoVariants} context - The context in which we'll find the elements.
   */
  #setShareUrl (context) {
    const shareButtons = getElements('share-button', { context })
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * Sets the active slide in the gallery carousel.
   *
   * @param {typeof window.BAO.Carousel.Element} galleryCarousel - The DOM element representing the gallery carousel.
   */
  #setSlide (galleryCarousel) {
    const wantedSlideIndex =
      galleryCarousel?.els?.slide?.elements?.findIndex(el =>
        el.dataset.variantIds?.split(',')?.includes(this.id.toString()),
      ) ?? -1
    if (wantedSlideIndex < 0) return

    galleryCarousel.carousel.moveToIdx(wantedSlideIndex)
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

window.BAO.Variant = Variant

export class BaoVariants extends window.BAO.CustomElement() {
  checkValidity () {
    super.checkValidity()

    if (!this.baseUrl) {
      this.throwError(`data-base-url is required`)
    }
  }

  setupListeners () {
    this.listeners.add(this, 'variant:change', this.onVariantChange.bind(this))
  }

  /**
   * @param {CustomEvent<{ variant: Variant }>} event
   */
  async onVariantChange (event) {
    const variant = event.detail.variant
    if (!variant) return

    variant.render(this, { updateUrl: this.updateUrl })
  }

  get baseUrl () {
    return this.dataset.baseUrl
  }

  get gallery () {
    const gallery = this.els?.gallery?.element
    if (!gallery) return null

    if (gallery instanceof window.BAO.Carousel.Element) {
      return gallery
    }

    return null
  }

  get options () {
    return this.els.options.element
  }

  get updateUrl () {
    return this.dataset.updateUrl === 'true'
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new window.BAO.Variant(this.defaultVariant)]
  }

  checkValidity () {
    super.checkValidity()

    const optionElExists = this.els?.option?.exists

    if (optionElExists) {
      const dataValue = this.els.option.element.dataset.value
      const optionChoiceExists = this.els?.['option-choice']?.exists

      if (!dataValue && !optionChoiceExists) {
        this.throwError(
          `[data-value] or a child element of "option-choice" is required when data-option is present`,
        )
      }
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  /**
   * @fires BaoVariants#variant:change
   */
  onVariantChange () {
    const variant = this.getVariant()

    const event = new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        variant,
      },
    })

    this.dispatchEvent(event)
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new window.BAO.Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every((option, index) => {
        return this.selectedOptions[index].value === option
      })
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice =
        getElements(this.els['option-choice'].selector, {
          context: el,
        }).find(el => el.checked)?.value ??
        this.els.option.element.dataset.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value:
          el.querySelector('select').value ||
          this.els.option.element.dataset.value,
      }
    })
  }
}

if (!customElements.get('bao-variants')) {
  customElements.define('bao-variants', BaoVariants)
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
